import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Store } from "lucide-react";
import { formatByCurrency } from "../../../utils/formatCurrency";
import TablePagination, { PAGE_SIZE } from "./TablePagination";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatPriceRange = (min, max, currency) => {
  if (min == null && max == null) return "—";
  if (min != null && max != null && min !== max) {
    return `${formatByCurrency(min, currency)} - ${formatByCurrency(max, currency)}`;
  }
  return formatByCurrency(min ?? max, currency);
};

// Our outlet products as a dense, scannable table (scales to hundreds of rows).
// Rows are grouped by outlet — the outlet name shows once per group with a
// divider — and a search box filters by outlet, product, or brand. The table
// body scrolls under a sticky header.
export default function OutletProductTable({ outlets = [], loading = false }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const flat = [];
    outlets.forEach((outlet) => {
      (outlet.products || []).forEach((product) => {
        flat.push({
          outlet_id: outlet.outlet_id,
          outlet_name: outlet.outlet_name,
          product_id: product.product_id,
          product_name: product.product_name,
          brand_name: product.brand_name,
          proofs: product.proofs,
          last_seen: product.last_seen,
          price_range_min: product.price_range_min,
          price_range_max: product.price_range_max,
          price_range_currency: product.price_range_currency,
        });
      });
    });
    return flat;
  }, [outlets]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    // Outlet is already filtered by the header dropdown, so search only the
    // product-level columns here.
    return rows.filter(
      (row) =>
        (row.product_name || "").toLowerCase().includes(query) ||
        (row.brand_name || "").toLowerCase().includes(query),
    );
  }, [rows, search]);

  // Jump back to page 1 whenever the result set changes underneath us — either
  // the user typed in the search box, or the parent refetched after an outlet /
  // date filter change. Without this, staying on page 4 of a now-2-page result
  // would show an empty table.
  useEffect(() => {
    setPage(1);
  }, [search, outlets]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  // Mark the first row of each outlet group (for the label + divider). Computed
  // after slicing so every page labels its own groups — otherwise a page that
  // starts mid-group would show a nameless outlet column.
  const display = useMemo(() => {
    const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    let previous = null;
    return pageRows.map((row) => {
      const firstOfGroup = row.outlet_id !== previous;
      previous = row.outlet_id;
      return { ...row, firstOfGroup };
    });
  }, [filtered, safePage]);

  const outletCount = useMemo(
    () => new Set(filtered.map((row) => row.outlet_id)).size,
    [filtered],
  );

  if (loading) {
    return <div className="dpi-chart-empty">{t("admin.insights.outletProducts.loading")}</div>;
  }
  if (!rows.length) {
    return <div className="dpi-chart-empty">{t("admin.insights.outletProducts.empty")}</div>;
  }

  return (
    <div className="dpi-op">
      <div className="dpi-op-toolbar">
        <div className="dpi-op-search">
          <Search size={15} />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("admin.insights.outletProducts.searchPlaceholder")}
          />
        </div>
        <span className="dpi-op-count">
          {t("admin.insights.outletProducts.tableSummary", {
            outlets: outletCount,
            products: filtered.length,
          })}
        </span>
      </div>

      <div className="dpi-op-scroll">
        <table className="dpi-op-table">
          <thead>
            <tr>
              <th className="dpi-op-th-outlet">{t("admin.insights.outletProducts.outlet")}</th>
              <th>{t("admin.insights.outletProducts.product")}</th>
              <th>{t("admin.insights.outletProducts.brand")}</th>
              <th>{t("admin.insights.outletProducts.priceRange")}</th>
              <th className="dpi-num">{t("admin.insights.outletProducts.proofs")}</th>
              <th>{t("admin.insights.outletProducts.lastSeen")}</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0 ? (
              <tr>
                <td colSpan={6} className="dpi-op-nomatch">
                  {t("admin.insights.outletProducts.noMatch")}
                </td>
              </tr>
            ) : (
              display.map((row) => (
                <tr
                  key={`${row.outlet_id}-${row.product_id}`}
                  className={row.firstOfGroup ? "dpi-op-row dpi-op-row--group" : "dpi-op-row"}
                >
                  <td className="dpi-op-outlet">
                    {row.firstOfGroup ? (
                      <span className="dpi-op-outlet-name" title={row.outlet_name}>
                        <Store size={13} />
                        {row.outlet_name}
                      </span>
                    ) : null}
                  </td>
                  <td className="dpi-op-product">{row.product_name}</td>
                  <td>
                    {row.brand_name ? (
                      <span className="dpi-own-brand">{row.brand_name}</span>
                    ) : (
                      <span className="dpi-muted">—</span>
                    )}
                  </td>
                  <td className="dpi-muted">
                    {formatPriceRange(
                      row.price_range_min,
                      row.price_range_max,
                      row.price_range_currency,
                    )}
                  </td>
                  <td className="dpi-num">{row.proofs}</td>
                  <td className="dpi-muted">{formatDate(row.last_seen)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={safePage}
        pageCount={pageCount}
        totalRows={filtered.length}
        onPageChange={setPage}
      />
    </div>
  );
}
